<template>
	<div>
		<!-- Filtres -->
		<div class="all-filter-container" v-if="showSearch">
			<form class="filter-desc-title">
				<img
					class="magnifying-glass"
					src="/img/magnifying-glass.svg"
					alt=""
				/>
				<input
					class="search-input"
					v-model="searchKeyword"
					type="text"
					placeholder="Rechercher par mots clefs"
				/>
			</form>
			<div class="all-filter-wrapper">
				<div class="filter-wrapper my-content">
					<select
						name="subject"
						id="subject"
						v-model="selectedSubject"
						@change="onSubjectChange"
					>
						<option value="">Toutes les matières</option>
						<option
							v-for="subject in subjects"
							:key="subject.id"
							:value="subject.id"
						>
							{{ subject.name }}
						</option>
					</select>
				</div>
				<div class="filter-wrapper">
					<select
						name="level"
						id="level"
						v-model="selectedLevel"
						@change="onLevelChange"
					>
						<option value="">Tous les niveaux</option>
						<option
							v-for="level in levels"
							:key="level.id"
							:value="level.id"
						>
							{{ level.name }}
						</option>
					</select>
				</div>
				<div class="filter-wrapper">
					<select
						class="filter-chapter"
						name="chapter"
						id="chapter"
						v-model="selectedChapter"
					>
						<option value="">Tous les chapitres</option>
						<optgroup
							v-for="sequence in availableSequences"
							:key="sequence.id"
							:label="sequence.name"
						>
							<option
								v-for="chapter in $store.getters.chapters(
									sequence.id
								)"
								:key="chapter.id"
								:value="chapter.id"
							>
								{{ chapter.name }}
							</option>
						</optgroup>
					</select>
				</div>
			</div>
		</div>

		<!-- Indicateur de chargement -->
		<!-- <div v-if="loading" class="loading-container">
            <p>Chargement des fichiers...</p>
        </div> -->

		<!-- Breadcrumb (style ClassDrive) -->
		<div v-if="showFolders && showBreadcrumb" class="path-btn-container">
			<p class="indicator-path">
				<span
					class="breadcrumb-root"
					@click="goRoot"
					style="cursor: pointer"
					>{{ rootLabel }}</span
				>
				<span v-if="breadcrumb && breadcrumb.length">
					<span
						v-for="(folder, idx) in breadcrumb"
						:key="folder.id"
						@click="goToBreadcrumb(idx)"
						style="cursor: pointer"
					>
						<span class="electric-blue"> &#62;</span>
						{{ folder.name }}
					</span>
				</span>
			</p>
		</div>

		<!-- Table des fichiers/dossiers -->
		<transition name="route" mode="out-in">
			<div
				v-if="
					!loading &&
					(sharedFiles.length > 0 ||
						(showFolders && displayedFolders.length > 0))
				"
				class="table__container"
				:key="navKey"
			>
				<table class="table">
					<colgroup>
						<col style="width: initial" />
						<col style="width: initial" />
						<col style="width: initial" />
						<col style="width: initial" />
						<col style="width: initial" />
					</colgroup>
					<thead>
						<tr>
							<th
								class="table__cell table__cell--center table__type"
							>
								Type
							</th>
							<th class="table__cell">Nom & description</th>
							<th class="table__cell table__cell--center">
								Niveau
							</th>
							<th class="table__cell table__cell--center">
								Matière
							</th>
							<th class="table__cell table__cell--center">
								Chapitre
							</th>
							<th class="table__cell table__cell--left">
								Partagé par
							</th>
						</tr>
					</thead>
					<tbody>
						<!-- Dossiers (optionnels) -->
						<template v-if="showFolders">
							<tr
								v-for="folder in displayedFolders"
								:key="`folder-${folder.id}`"
								class="table__main"
								style="cursor: pointer"
								@dblclick="openFolder(folder)"
							>
								<td class="table__cell table__cell--center">
									<img
										class="table__icon"
										src="/img/file-types/folder.svg"
										alt="Dossier"
									/>
								</td>
								<td class="table__cell">
									<span class="table__name">{{
										folder.name
									}}</span>
									<button
										class="table__button button button--secondary button--small"
										@click.stop="openFolder(folder)"
									>
										Ouvrir
									</button>
								</td>
								<td class="table__cell table__cell--center">
									{{ folder?.level?.name || "---" }}
								</td>
								<td class="table__cell table__cell--center">
									{{ folder?.subject_name || "---" }}
								</td>
								<td class="table__cell table__cell--center">
									{{ folder?.chapter_name || "---" }}
								</td>
								<td class="table__cell table__cell--left">
									<span
										class="table__author--redirect"
										v-on:click="
											openProfile(
												folder.teacher
													?.community_profile_id
											)
										"
									>
										<span class="table__author">{{
											getInitials(folder.teacher)
										}}</span>
										<span
											>{{ folder.teacher?.first_name }}
											{{
												folder.teacher?.last_name
											}}</span
										>
									</span>
								</td>
							</tr>
						</template>
						<tr
							v-for="sharedFile in sharedFiles"
							:key="sharedFile.id"
							class="table__main"
							@dblclick="downloadFile(sharedFile)"
							style="cursor: pointer"
						>
							<td class="table__cell table__cell--center">
								<img
									class="table__icon"
									:src="getFileLogo(sharedFile.name)"
									:alt="getFileTypeName(sharedFile.name)"
								/>
							</td>
							<td class="table__cell">
								<span class="table__name">{{
									sharedFile.name
								}}</span>
								<!-- <br /><span class="table__description">Description</span> -->
								<button
									v-if="canPreviewFile(sharedFile.name)"
									class="table__button button button--secondary button--small"
									@click.stop="showFilePreview(sharedFile)"
								>
									Aperçu
								</button>
								<button
									v-else
									class="table__button button button--secondary button--small"
									@click.stop="downloadFile(sharedFile)"
								>
									Télécharger
								</button>
							</td>
							<td class="table__cell table__cell--center">
								{{ sharedFile?.level?.name || "---" }}
							</td>
							<td class="table__cell table__cell--center">
								{{ sharedFile?.subject?.name || "---" }}
							</td>
							<td class="table__cell table__cell--center">
								{{ sharedFile?.chapter?.name || "---" }}
							</td>
							<td class="table__cell table__cell--left">
								<span
									class="table__author--redirect"
									v-on:click="
										openProfile(
											sharedFile.teacher
												.community_profile_id
										)
									"
								>
									<span class="table__author">{{
										getInitials(sharedFile.teacher)
									}}</span>
									<span
										>{{ sharedFile.teacher.first_name }}
										{{ sharedFile.teacher.last_name }}</span
									>
								</span>
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		</transition>

		<!-- Message quand il n'y a pas de résultats -->
		<div
			v-if="
				!loading &&
				totalCount === 0 &&
				(!showFolders || displayedFolders.length === 0)
			"
			class="no-results"
		>
			<p>Aucun fichier trouvé avec les critères sélectionnés.</p>
		</div>

		<!-- Pagination -->
		<PaginationComponent
			v-if="totalPages > 1"
			:totalPages="totalPages"
			:currentPage="currentPage"
			:perPage="10"
			@pageChanged="$emit('page-changed', $event)"
		/>

		<!-- Modal d'aperçu -->
		<FilePreview
			:show="showNotice"
			:filePath="currentFilePath"
			title="Aperçu du fichier"
			:showDownloadButton="true"
			:fileName="currentFileName"
			:customDownloadFunction="() => downloadFile(currentSharedFile)"
			@close="showNotice = false"
			@error="handlePreviewError"
		/>

		<!-- Popup accès restreint -->
		<RestrictedAccessPopup
			v-if="showRestrictedPopup"
			:email="restrictedEmail"
			@close="showRestrictedPopup = false"
		/>
	</div>
</template>

<script>
import { downloadMedia } from "../../../../../services/mediaService";
import { authenticatedApiFetch } from "../../../../../utils/utils";
import {
	getFileLogo,
	getFileTypeName,
	canPreviewFile,
} from "../../../../../utils/fileTypes";
import FilePreview from "../../../../FilePreview/FilePreview.vue";
import RestrictedAccessPopup from "../../../../PopUp/RestrictedAccessPopup.vue";
import PaginationComponent from "../../../../Utils/PaginationComponent.vue";

export default {
	name: "SharedFilesTable",
	components: {
		FilePreview,
		PaginationComponent,
		RestrictedAccessPopup,
	},
	emits: [
		"page-changed",
		"filters-changed",
		"open-folder",
		"go-back-folder",
		"go-root",
		"go-breadcrumb",
	],
	props: {
		sharedFiles: {
			type: Array,
			default: () => [],
		},
		// Active l'affichage des dossiers (désactivé par défaut pour ne pas impacter les autres usages)
		showFolders: {
			type: Boolean,
			default: false,
		},
		// Liste des dossiers à afficher quand showFolders = true
		sharedFolders: {
			type: Array,
			default: () => [],
		},
		totalCount: {
			type: Number,
			default: 0,
		},
		currentPage: {
			type: Number,
			default: 0,
		},
		totalPages: {
			type: Number,
			default: 0,
		},
		loading: {
			type: Boolean,
			default: false,
		},
		showSearch: {
			type: Boolean,
			default: true,
		},
		useFilterParam: {
			type: Boolean,
			default: false,
		},
		// Contrôle externe du bouton Remonter
		canGoBack: {
			type: Boolean,
			default: false,
		},
		showBreadcrumb: {
			type: Boolean,
			default: true,
		},
		breadcrumb: {
			type: Array,
			default: () => [],
		},
	},
	data() {
		return {
			selectedSubject: "",
			selectedLevel: "",
			selectedChapter: "",
			searchKeyword: "",
			showNotice: false,
			currentFilePath: "",
			currentFileName: "",
			currentSharedFile: null,
			searchTimeout: null,
			showRestrictedPopup: false,
			restrictedEmail: "contact@lecoinduprof.fr",
		};
	},
	computed: {
		subjects() {
			return this.$store.state.subjects || [];
		},
		levels() {
			return this.$store.state.levels || [];
		},
		availableChapters() {
			if (!this.selectedSubject || !this.selectedLevel) {
				return [];
			}

			const sequences = this.$store.getters.sequencesByLevelAndSubject(
				this.selectedLevel,
				this.selectedSubject
			);
			if (!sequences || sequences.length === 0) {
				return [];
			}

			const chapters = [];
			sequences.forEach((sequence) => {
				const sequenceChapters = this.$store.getters.chapters(
					sequence.id
				);
				if (sequenceChapters && sequenceChapters.length > 0) {
					chapters.push(...sequenceChapters);
				}
			});

			return chapters;
		},
		availableSequences() {
			if (!this.selectedSubject || !this.selectedLevel) {
				return [];
			}
			return this.$store.getters.sequencesByLevelAndSubject(
				this.selectedLevel,
				this.selectedSubject
			);
		},
		rootLabel() {
			return "Mes fichiers";
		},
		displayedFolders() {
			// Afficher les dossiers uniquement sur la première page
			if (this.currentPage && this.currentPage > 0) return [];
			let folders = [];
			if (!this.breadcrumb || this.breadcrumb.length === 0) {
				folders = (this.sharedFolders || []).filter(
					(f) => f.parent_folder_id === null
				);
			} else {
				folders = this.sharedFolders || [];
			}
			return this.filterItems(folders);
		},
		navKey() {
			const ids = (this.breadcrumb || []).map((b) => b.id).join("-");
			return `nav-${ids}-${this.sharedFiles.length}-${this.displayedFolders.length}`;
		},
	},
	methods: {
		// Navigation dossiers
		openFolder(folder) {
			if (!folder || !folder.id) return;
			this.$emit("open-folder", folder);
		},
		goBackFolder() {
			this.$emit("go-back-folder");
		},
		goRoot() {
			this.$emit("go-root");
		},
		goToBreadcrumb(index) {
			this.$emit("go-breadcrumb", index);
		},

		getInitials(teacher) {
			return `${teacher.first_name[0]}${teacher.last_name[0]}`;
		},
		onSubjectChange() {
			this.selectedChapter = "";
			this.updateURL();
		},
		onLevelChange() {
			this.selectedChapter = "";
			this.updateURL();
		},
		emitFiltersChange() {
			const filters = {};
			if (this.selectedSubject) filters.subject = this.selectedSubject;
			if (this.selectedLevel) filters.level = this.selectedLevel;
			if (this.selectedChapter) filters.chapter = this.selectedChapter;
			if (this.searchKeyword)
				filters[this.useFilterParam ? "filter" : "search"] =
					this.searchKeyword;

			this.$emit("filters-changed", filters);
		},
		updateURL() {
			const query = {};

			if (this.selectedSubject) query.subject = this.selectedSubject;
			if (this.selectedLevel) query.level = this.selectedLevel;
			if (this.selectedChapter) query.chapter = this.selectedChapter;
			if (this.searchKeyword)
				query[this.useFilterParam ? "filter" : "search"] =
					this.searchKeyword;

			if (JSON.stringify(query) !== JSON.stringify(this.$route.query)) {
				this.$router.replace({ query });
			}
		},
		loadFiltersFromURL() {
			const query = this.$route.query;

			this.selectedSubject = query.subject || "";
			this.selectedLevel = query.level || "";
			this.selectedChapter = query.chapter || "";
			this.searchKeyword = this.useFilterParam
				? query.filter || ""
				: query.search || "";

			this.emitFiltersChange();
		},
		filterItems(items) {
			let filtered = items;

			// Filtre matière
			if (this.selectedSubject) {
				filtered = filtered.filter((item) => {
					// Pour les fichiers
					if (item.subject?.id) {
						return (
							String(item.subject.id) ===
							String(this.selectedSubject)
						);
					}
					// Pour les dossiers
					if (item.subject) {
						return (
							String(item.subject) ===
							String(this.selectedSubject)
						);
					}
					return false;
				});
			}
			// Filtre niveau
			if (this.selectedLevel) {
				filtered = filtered.filter((item) => {
					if (item.level?.id) {
						return (
							String(item.level.id) === String(this.selectedLevel)
						);
					}
					if (item.level_id) {
						return (
							String(item.level_id) === String(this.selectedLevel)
						);
					}
					return false;
				});
			}
			// Filtre chapitre
			if (this.selectedChapter) {
				filtered = filtered.filter((item) => {
					if (item.chapter?.id) {
						return (
							String(item.chapter.id) ===
							String(this.selectedChapter)
						);
					}
					if (item.chapter) {
						return (
							String(item.chapter) ===
							String(this.selectedChapter)
						);
					}
					return false;
				});
			}
			// Filtre recherche
			if (this.searchKeyword) {
				const keyword = this.searchKeyword.toLowerCase();
				filtered = filtered.filter(
					(item) =>
						(item.name &&
							item.name.toLowerCase().includes(keyword)) ||
						(item.description &&
							item.description.toLowerCase().includes(keyword))
				);
			}
			return filtered;
		},
		getLevelName() {
			if (!this.selectedLevel) return null;
			const level = this.levels.find((l) => l.id == this.selectedLevel);
			return level ? level.name : null;
		},
		getSubjectName() {
			if (!this.selectedSubject) return null;
			const subject = this.subjects.find(
				(s) => s.id == this.selectedSubject
			);
			return subject ? subject.name : null;
		},
		getChapterName() {
			if (!this.selectedChapter) return null;
			const chapter = this.availableChapters.find(
				(c) => c.id == this.selectedChapter
			);
			return chapter ? chapter.name : null;
		},
		getFileLogo(filename) {
			return getFileLogo(filename);
		},
		getFileTypeName(filename) {
			return getFileTypeName(filename);
		},
		canPreviewFile(filename) {
			return canPreviewFile(filename);
		},
		async showFilePreview(sharedFile) {
			try {
				// Stocker le fichier partagé pour le téléchargement
				this.currentSharedFile = sharedFile;

				// Récupérer les détails complets du fichier via l'API /files/
				// pour obtenir le vrai chemin du fichier (file_upload)
				const fileDetailsResponse = await authenticatedApiFetch(
					`/files/${sharedFile.id}/`,
					{ method: "GET" }
				);

				if (fileDetailsResponse.ok) {
					const fileDetails = await fileDetailsResponse.json();

					if (fileDetails.file_upload) {
						this.currentFilePath = fileDetails.file_upload;
					}
				}

				this.currentFileName = sharedFile.name;
				this.showNotice = true;
			} catch (error) {
				console.error(
					"Erreur lors de la récupération des détails du fichier:",
					error
				);
			}
		},
		handlePreviewError(error) {
			console.error("Erreur lors de l'affichage de l'aperçu:", error);
		},
		async downloadFile(sharedFile) {
			console.log("downloadFile", sharedFile);
			// Bloque le téléchargement si l'accès n'est pas partagé avec moi
			if (sharedFile && sharedFile.is_shared_with_me === false) {
				if (sharedFile.teacher.email) {
					this.restrictedEmail = sharedFile.teacher.email;
				}
				// Ferme l'aperçu avant d'ouvrir la pop-up restreinte pour éviter le chevauchement
				this.showNotice = false;
				this.$nextTick(() => {
					this.showRestrictedPopup = true;
				});
				return;
			}
			try {
				// Récupérer les détails complets du fichier via l'API /files/
				// pour obtenir le vrai chemin du fichier (file_upload)
				const fileDetailsResponse = await authenticatedApiFetch(
					`/files/${sharedFile.id}/`,
					{ method: "GET" }
				);

				if (fileDetailsResponse.ok) {
					const fileDetails = await fileDetailsResponse.json();

					if (fileDetails.file_upload) {
						await downloadMedia(
							fileDetails.file_upload,
							sharedFile.name
						);

						this.$store.state.toast = {
							status: "success",
							text: "Fichier téléchargé avec succès",
						};
						return;
					}
				}

				this.$store.state.toast = {
					status: "success",
					text: "Fichier téléchargé avec succès",
				};
			} catch (error) {
				console.error("Erreur lors du téléchargement:", error);
				this.$store.state.toast = {
					status: "error",
					text: "Erreur lors du téléchargement du fichier",
				};
			}
		},
		openProfile(id) {
			if (!id) return;
			this.$router.push({
				name: "CommunityProfilePublic",
				params: { id },
			});
		},
	},
	created() {
		this.loadFiltersFromURL();
	},
	beforeUnmount() {
		if (this.searchTimeout) {
			clearTimeout(this.searchTimeout);
		}
	},
	watch: {
		selectedSubject() {
			this.updateURL();
			this.emitFiltersChange();
		},
		selectedLevel() {
			this.updateURL();
			this.emitFiltersChange();
		},
		selectedChapter() {
			this.updateURL();
			this.emitFiltersChange();
		},
		searchKeyword() {
			clearTimeout(this.searchTimeout);
			this.searchTimeout = setTimeout(() => {
				this.updateURL();
				this.emitFiltersChange();
			}, 500);
		},

		"$route.query": {
			handler(newQuery, oldQuery) {
				if (JSON.stringify(newQuery) !== JSON.stringify(oldQuery)) {
					this.loadFiltersFromURL();
				}
			},
			deep: true,
		},
	},
};
</script>

<style scoped>
.loading-container {
	text-align: center;
	padding: 40px;
	color: #666;
}

.results-info {
	margin: 10px 0;
	color: #666;
	font-size: 14px;
}

.results-info p {
	margin: 0;
}

.no-results {
	text-align: center;
	padding: 40px;
	color: #666;
	font-style: italic;
}

.no-results p {
	margin: 0;
	font-size: 16px;
}

.table__author--redirect {
	cursor: pointer;
	box-shadow: 0 0 2px 0 rgba(0, 0, 0, 0);
	border: 1px solid transparent;
}

.table__author--redirect:hover,
.table__author--redirect:focus {
	box-shadow: 0 0 2px 0 rgba(0, 0, 0, 0.15);
	border-radius: 8px;
	background: white;
	border: 1px solid #d6dded;
}
/* Breadcrumb styles matching drive */
.path-btn-container {
	display: flex;
	justify-content: space-between;
	align-items: center;
}
.indicator-path {
	margin: 0 0 12px 0;
}
.electric-blue {
	color: #5b7cff;
}
.breadcrumb-root {
	color: #3182ce;
}
</style>
